import { useEffect } from "react";

/**
 * Lock page scroll while a sheet/dialog is open.
 *
 * `document.body.style.overflow = "hidden"` does NOT work on iOS Safari —
 * touch / momentum scroll passes through to the page underneath, and a
 * swipe-down on the bottom-sheet ends up scrolling the page behind it.
 * The robust fix is to pin the body via `position: fixed` + `top: -scrollY`
 * + `width: 100%` and restore the saved scroll position on release.
 *
 * Refcounted so multiple stacked sheets nest correctly.
 *
 * Leak hardening:
 * - State lives on `window` (not module-level) so dev HMR / fast-refresh
 *   don't drop the refcount mid-lock and orphan body styles.
 * - `pagehide` (incl. bfcache freeze) forces a hard reset.
 * - `useScrollLockSafetyNet()` detects pathname changes and clears stale
 *   styles whenever no sheet/dialog is actually mounted — covers the rare
 *   case where a sheet unmounts asynchronously across navigation.
 *
 * Both apps' ResponsiveSheet shells use this lock; `searchable-dropdown`,
 * `universal-search` and `guided-search-wizard` also call it directly when
 * they render their own bottom-sheet overlays.
 */

// Trace logging. Default ON in dev for active debugging; silence with
//   window.__pontimedScrollLockDebug = false
// in DevTools. Stays silent in prod regardless.
//
// Reads NODE_ENV via a typed-loose `globalThis.process` lookup so this
// package compiles without `@types/node`. Both Vite and Next replace the
// expression at build time, so the prod branch is tree-shaken.
const isProd = (() => {
  const proc = (globalThis as { process?: { env?: { NODE_ENV?: string } } }).process;
  return proc?.env?.NODE_ENV === "production";
})();
const log = (...args: unknown[]) => {
  if (typeof window === "undefined") return;
  if (isProd) return;
  const flag = (window as { __pontimedScrollLockDebug?: boolean }).__pontimedScrollLockDebug;
  if (flag === false) return;
  console.log("[scroll-lock]", ...args);
};

function describeCaller(): string {
  // Skip the first 3 frames (Error, this fn, the caller in this file).
  const stack = new Error().stack?.split("\n").slice(3, 6) ?? [];
  return stack.map((s) => s.trim()).join(" ← ");
}

function snapshotModals(): string {
  if (typeof document === "undefined") return "(ssr)";
  const els = document.querySelectorAll<HTMLElement>(
    '[data-slot="sheet-content"], [data-slot="dialog-content"]',
  );
  if (!els.length) return "(none)";
  return Array.from(els)
    .map((el) => `${el.dataset.slot}[side=${el.dataset.side ?? "?"}]`)
    .join(", ");
}

interface LockState {
  count: number;
  savedScrollY: number;
  savedStyles: {
    position: string;
    top: string;
    width: string;
    overflow: string;
  } | null;
}

const STATE_KEY = "__pontimedScrollLockState" as const;

type LockWindow = Window & { [STATE_KEY]?: LockState };

function getState(): LockState | null {
  if (typeof window === "undefined") return null;
  const w = window as LockWindow;
  if (!w[STATE_KEY]) {
    w[STATE_KEY] = { count: 0, savedScrollY: 0, savedStyles: null };
  }
  return w[STATE_KEY];
}

function applyLock() {
  const state = getState();
  if (!state) return;
  if (state.count > 0) {
    state.count += 1;
    log(
      `applyLock — refcount bump → ${state.count}, modals: ${snapshotModals()}`,
      describeCaller(),
    );
    return;
  }
  state.savedScrollY = window.scrollY;
  state.savedStyles = {
    position: document.body.style.position,
    top: document.body.style.top,
    width: document.body.style.width,
    overflow: document.body.style.overflow,
  };
  document.body.style.position = "fixed";
  document.body.style.top = `-${state.savedScrollY}px`;
  document.body.style.width = "100%";
  document.body.style.overflow = "hidden";
  state.count = 1;
  log(
    `applyLock — first lock, scrollY=${state.savedScrollY}, modals: ${snapshotModals()}`,
    describeCaller(),
  );
}

function releaseLock() {
  const state = getState();
  if (!state || state.count === 0) {
    log(
      `releaseLock — IGNORED (count=${state?.count ?? "?"}), body.position=${document.body.style.position || "(empty)"}, modals: ${snapshotModals()}`,
      describeCaller(),
    );
    return;
  }
  state.count -= 1;
  if (state.count > 0) {
    log(`releaseLock — refcount drop → ${state.count}, modals: ${snapshotModals()}`);
    return;
  }
  log(`releaseLock — final unlock, restoring scrollY=${state.savedScrollY}`);
  restoreBodyStyles(state);
}

function restoreBodyStyles(state: LockState) {
  if (state.savedStyles) {
    document.body.style.position = state.savedStyles.position;
    document.body.style.top = state.savedStyles.top;
    document.body.style.width = state.savedStyles.width;
    document.body.style.overflow = state.savedStyles.overflow;
    state.savedStyles = null;
  } else {
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";
    document.body.style.overflow = "";
  }
  window.scrollTo(0, state.savedScrollY);
}

/**
 * Hard reset — clears body styles + zeroes the refcount regardless of state.
 * Restores scroll position from the saved state (or by parsing `body.style.top`
 * when state was dropped — e.g. across HMR), so the page stays where the user
 * left it instead of snapping to the top. No reload, no React re-render.
 * Reserved for failsafe paths (pagehide, leak detected on navigation/mutation).
 */
function forceRelease() {
  const state = getState();
  if (!state) return;
  const wasFixed = document.body.style.position === "fixed";
  if (wasFixed) {
    // Recover the scroll offset the lock was hiding. Prefer state.savedScrollY
    // (always populated when applyLock ran in this session); fall back to the
    // -Npx value still pinned on the body (covers HMR / state-loss cases).
    let restoreY = state.savedScrollY;
    if (!restoreY) {
      const match = /^-(\d+)px$/.exec(document.body.style.top);
      if (match) restoreY = parseInt(match[1], 10);
    }
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";
    document.body.style.overflow = "";
    if (restoreY) window.scrollTo(0, restoreY);
  }
  log(
    `forceRelease — body was ${wasFixed ? "FIXED (reset)" : "clean"}, prev count=${state.count}`,
    describeCaller(),
  );
  state.count = 0;
  state.savedStyles = null;
}

/** Lock body scroll while `locked` is true. Safe to call from multiple places. */
export function useBodyScrollLock(locked: boolean): void {
  useEffect(() => {
    if (!locked) return;
    applyLock();
    return () => releaseLock();
  }, [locked]);
}

/**
 * Mount once near the top of the tree (root layout / locale layout for
 * Next.js, root route for TanStack Router). Catches orphaned body styles in
 * three scenarios:
 *  1. `pagehide` — clean up before the page is frozen / unloaded so a back
 *     navigation doesn't restore a fixed body.
 *  2. Pathname change while body is `position: fixed` but our refcount is 0
 *     — a sheet unmounted without firing cleanup (HMR mid-modal, navigation
 *     while modal closing, etc.).
 *  3. **Any DOM mutation** (MutationObserver on `<body>`): same refcount-vs-
 *     -body-style check on every batch, microtask-deferred. Covers cases where
 *     Portal cleanup didn't fire.
 *
 * The check is **refcount-based**, not DOM-based — DOM presence of a modal
 * element is unreliable (custom sheets, framer-motion drawers, ad-hoc portals
 * don't all share a common selector). Refcount is the source of truth: if any
 * consumer holds a lock, leave it alone.
 *
 * Tied to `pathname` via the caller so it re-runs on every route change.
 */
function checkAndReleaseIfStale() {
  if (typeof document === "undefined") return;
  if (document.body.style.position !== "fixed") return;
  const state = getState();
  if (state && state.count > 0) return;
  log(`safety-net — stale lock detected (body fixed, refcount=0), forcing release`);
  forceRelease();
}

export function useScrollLockSafetyNet(pathname: string | null): void {
  useEffect(() => {
    if (typeof document === "undefined") return;
    checkAndReleaseIfStale();
  }, [pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => forceRelease();
    // pagehide fires before unload + on bfcache freeze; covers the case where
    // the page is about to be persisted with a fixed body.
    window.addEventListener("pagehide", handler);
    return () => window.removeEventListener("pagehide", handler);
  }, []);

  // MutationObserver on <body>. Fires the same check after every modal
  // mount/unmount — the cheap path is the early-return when body isn't fixed.
  useEffect(() => {
    if (typeof document === "undefined" || typeof MutationObserver === "undefined") return;
    let scheduled = false;
    const flush = () => {
      scheduled = false;
      checkAndReleaseIfStale();
    };
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      // Defer to a microtask so we observe the DOM after the batch settles
      // (an unmounting sheet may briefly coexist with another opening one).
      queueMicrotask(flush);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
}
